// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/ivr_list.js (611 lines). BYTE-IDENTICAL.
 *
 * IVR list with Normal / Missed tabs (Missed tab gated by canAccessMissedIvr),
 * per-row Edit/Clone/Delete actions, multi-delete, client-side flow_name search,
 * ACL scaffold + refresh listener, flash-message alert marquee, CSV export.
 *
 * PrimeReact DataTable â†’ obd3-gui DataTable (client-side, no server pagination);
 * PrimeReact Checkbox â†’ native input; PrimeReact Button â†’ react-bootstrap Button;
 * PrimeReact filters state kept but dead (DataTable doesn't use it â€” harmless).
 *
 * v1 quirks preserved:
 *  - `RenderActionComponent` state reads `selectedIVR` from outer scope, so
 *    the delete-icon hide-when-selecting behaviour depends on that closure.
 *  - `id` used in `handleDeleteIVR(id)` is `flow_id` from row data (per
 *    `actionBodyTemplate`) â€” passed through unchanged.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
// status: 8 = draft (autosaved, not yet Saved by the user), -1 = pending
// (saved but not yet marked complete), anything else = complete.
const getIvrStatusLabel = (status: any) =>
  status == 8 ? 'Draft' : status == -1 ? 'Pending' : 'Complete';
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { API_IVR_LIST, API_IVR_DELETE } from '@/service/url';
import {
  getApiCall,
  postApiCall,
  fetchAndSetFlashMessage,
} from '@/service/httpCall';
import { toast } from 'sonner';
import {
  ROUTES,
  USER_ROLES,
  MODULE_ACL,
  FLASH_MESSAGE_ACTION_OBJECT,
} from '@/utilities/constant';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import Loader from '@/components/partials/components/Loader';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { canAccessMissedIvr } from '@/utilities/ivrAcl';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Plus } from 'lucide-react';
import '@/assets/custom/css/ivr-editor.css';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const EditIcon = ({ linkTo }: any) => (
  <Link className="btn btn-sm btn-icon btn-warning" title="Edit" to={linkTo}>
    <span className="btn-inner">
      <svg width="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.4925 2.78906H7.75349C4.67849 2.78906 2.75049 4.96606 2.75049 8.04806V16.3621C2.75049 19.4441 4.66949 21.6211 7.75349 21.6211H16.5775C19.6625 21.6211 21.5815 19.4441 21.5815 16.3621V12.3341" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path fillRule="evenodd" clipRule="evenodd" d="M8.82812 10.921L16.3011 3.44799C17.2321 2.51799 18.7411 2.51799 19.6721 3.44799L20.8891 4.66499C21.8201 5.59599 21.8201 7.10599 20.8891 8.03599L13.3801 15.545C12.9731 15.952 12.4211 16.181 11.8451 16.181H8.09912L8.19312 12.401C8.20712 11.845 8.43412 11.315 8.82812 10.921Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.1655 4.60254L19.7315 9.16854" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  </Link>
);

const CloneIcon = ({ linkTo }: any) => (
  <Link className="btn btn-sm btn-icon btn-info" title="Clone" to={linkTo}>
    <span className="btn-inner">
      <svg width="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="8" y="8" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </span>
  </Link>
);

const DeleteIcon = ({ handleCallback }: any) => (
  <Link
    className="btn btn-sm btn-icon btn-danger"
    title="Delete"
    to="#"
    onClick={(e) => {
      e.preventDefault();
      handleCallback();
    }}
  >
    <span className="btn-inner">
      <svg width="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
        <path d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20.708 6.23975H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  </Link>
);

const IvrList = () => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const { cid } = useParams();

  const [IvrList, setIvrList] = useState<any[]>([]);
  const [originalIvrList, setOriginalIvrList] = useState<any[]>([]);
  const [searchValue, setSearchValue] = usePersistedState('ivr/IvrList:searchValue', '');
  const [activeTab, setActiveTab] = usePersistedState('ivr/IvrList:activeTab', 'normal');
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteIvrId, setDeleteIvrId] = useState<any>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedIVR, setSelectedIVR] = useState<any[]>([]);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [userLoadingDelete, setUserLoadingDelete] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [alertMessage, setAlertMessage] = useState<any>(null);

  const handleExport = async () => {
    setButtonDisable(true);
    exportToCSV({
      fileName: 'ivr-list',
      data: IvrList,
      columns: [
        { header: 'Flow Name', accessor: 'flow_name' },
        {
          header: 'IVR Category',
          accessor: (row: any) => row.ivr_category || 'normal',
        },
        {
          header: 'Created At',
          accessor: (row: any) =>
            new Date(row.created_at).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }),
        },
        {
          header: 'Last Updated At',
          accessor: (row: any) =>
            new Date(row.updated_at).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }),
        },
        {
          header: 'Status',
          accessor: (row: any) => getIvrStatusLabel(row.status),
        },
      ],
    });
    setButtonDisable(false);
  };

  const handleCheckboxToggle = (ivrId: any) => {
    const isSelected = selectedIVR.includes(ivrId);
    setSelectedIVR(
      isSelected
        ? selectedIVR.filter((id) => id !== ivrId)
        : [...selectedIVR, ivrId],
    );
  };

  const handleMultiDelete = () => {
    setIsMultiDelete(true);
    setEnableDeleteModal(true);
  };

  const fetchIvrList = () => {
    setLoading(true);
    const apiIvrListUrl = API_IVR_LIST.replace(':cid', cid);
    getApiCall(apiIvrListUrl, {})
      .then(onIvrListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
  };

  const handleClear = () => {
    setSearchValue('');
    fetchIvrList();
  };

  useEffect(() => {
    fetchIvrList();
    const user = getUser();
    if (user.role === USER_ROLES.SUPERADMIN) {
      setIsSuperadmin(true);
    }

    if (user?.acl_settings) {
      setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    }

    fetchAndSetFlashMessage(
      (msg: any) =>
        msg.module_name === MODULE_ACL.IVR &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.LIST &&
        msg.type === 'Flash Message',
      setAlertMessage,
    );
  }, []);

  const ivrAclContext = useMemo(
    () => ({
      userRole: getUser()?.role,
      isSuperAdmin,
    }),
    [isSuperAdmin],
  );

  const canMissedIvr = useMemo(
    () => canAccessMissedIvr(loggedInUserACL, ivrAclContext),
    [loggedInUserACL, ivrAclContext],
  );

  useEffect(() => {
    if (
      activeTab === 'missed' &&
      !canMissedIvr &&
      Object.keys(loggedInUserACL).length > 0
    ) {
      setActiveTab('normal');
    }
  }, [activeTab, canMissedIvr, loggedInUserACL]);

  const handleSelectAll = () => {
    if (selectedIVR.length === IvrList.length) {
      setSelectedIVR([]);
    } else {
      const allAclIds = IvrList.map((ivr: any) => ivr.flow_id);
      setSelectedIVR(allAclIds);
    }
  };

  useEffect(() => {
    if (confirmDelete) {
      setUserLoadingDelete(true);
      if (isMultiDelete) {
        postApiCall(API_IVR_DELETE.replace(':cid', cid), {
          ivrIds: selectedIVR,
        })
          .then(onDeleteSuccess)
          .catch(onDeleteFail);
      } else {
        postApiCall(API_IVR_DELETE.replace(':cid', cid), {
          ivrIds: [deleteIvrId],
        })
          .then(onDeleteSuccess)
          .catch(onDeleteFail);
      }
    }
  }, [confirmDelete]);

  useEffect(() => {
    const byCategory = originalIvrList.filter((ivr: any) => {
      const cat = String(ivr.ivr_category || 'normal').toLowerCase();
      return activeTab === 'missed' ? cat === 'missed' : cat !== 'missed';
    });

    if (searchValue.trim()) {
      const filteredData = byCategory.filter((ivr: any) =>
        ivr.flow_name.toLowerCase().includes(searchValue.trim().toLowerCase()),
      );
      setIvrList(filteredData);
    } else {
      setIvrList(byCategory);
    }
    setSelectedIVR([]);
  }, [searchValue, originalIvrList, activeTab]);

  function onDeleteSuccess(resp: any) {
    const { deleted = [], notDeleted = [] } = resp;

    toast.success(
      <div>
        {deleted.length > 0 && (
          <div>
            <strong>Deleted:</strong> {deleted.join(', ')}
          </div>
        )}

        {notDeleted.length > 0 && (
          <div style={{ marginTop: '5px' }}>
            <strong>Not deleted:</strong>
            {notDeleted.map((item: any, index: number) => (
              <div key={index}>
                {item.ivrName} ({item.reason})
              </div>
            ))}
          </div>
        )}
      </div>,
    );
    let updatedList: any[];
    if (isMultiDelete) {
      updatedList = IvrList.filter(
        (val: any) => !selectedIVR.includes(val.flowId),
      ).map((val: any, key: number) => {
        val['key'] = key + 1;
        return val;
      });
    } else {
      updatedList = IvrList.filter((val: any) => val.flowId != deleteIvrId).map(
        (val: any, key: number) => {
          val['key'] = key + 1;
          return val;
        },
      );
    }

    setLoading(true);
    const apiIvrListUrl = API_IVR_LIST.replace(':cid', cid);
    getApiCall(apiIvrListUrl, {})
      .then(onIvrListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));

    setIvrList(updatedList);
    setIsMultiDelete(false);
    setEnableDeleteModal(false);
    setDeleteIvrId(undefined);
    setConfirmDelete(false);
    setUserLoadingDelete(false);
  }

  const [
    moduleOperationsForReadPermissionCheck,
    setModuleOperationsForReadPermissionCheck,
  ] = useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  function onDeleteFail(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.warning(`Unable to delete IVR: ${err}`);
    }
    setUserLoadingDelete(false);
    setConfirmDelete(false);
  }

  function onIvrListSuccess(resp: any) {
    for (let idx in resp) {
      resp[idx]['key'] = Number(idx) + 1;
      resp[idx]['created_at'] = new Date(resp[idx]['created_at']).toGMTString();
    }
    setIvrList(resp);
    setOriginalIvrList(resp);
  }

  function handleDeleteIVR(id: any) {
    checkAuth(setToastProps, setShowToast);
    setIsMultiDelete(false);
    setEnableDeleteModal(true);
    setDeleteIvrId(id);
  }

  const RenderActionComponent = ({ id, category }: any) => {
    const editUrl = ROUTES.IVR.EDIT.replace(':id', id).replace(':cid', cid);
    const cloneCategory = category || 'normal';
    const cloneUrl = ROUTES.IVR.CREATE.replace(':cid', cid);
    const canCloneMissed = cloneCategory !== 'missed' || canMissedIvr;

    return (
      <div
        className="flex align-items-center list-user-action"
        style={{ gap: '8px' }}
      >
        {canEdit && <EditIcon linkTo={`/${editUrl}`} />}
        {canAdd && canCloneMissed && (
            <CloneIcon
              linkTo={`/${cloneUrl}?category=${cloneCategory}&cloneId=${id}`}
            />
          )}
        {!(selectedIVR.length > 0) && canDelete && (
            <DeleteIcon handleCallback={() => handleDeleteIVR(id)} />
          )}
      </div>
    );
  };

  function onError(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleCreateButtonClick = () => {
    if (activeTab === 'missed' && !canMissedIvr) {
      toast.error(
        'You do not have permission to create Missed Type IVR flows.',
      );
      return;
    }
    const createUrl = ROUTES.IVR.CREATE.replace(':cid', cid);
    navigate(`/${createUrl}?category=${activeTab}`);
  };

  const isCallCentreAdmin = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.IVR,
    'read',
  );
  const canAdd =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.IVR, 'add');
  const canEdit =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.IVR, 'edit');
  const canDelete =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.IVR, 'delete');

  if (!canRead) {
    return (
      <div
        className="text-center p-4"
        style={{
          marginTop: '50px',
          marginBottom: '50px',
          fontSize: '16px',
        }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) {
    return <Loader />;
  }

  const columns: ColumnDef<any>[] = [
    {
      id: 'select',
      header: () => (
        <input
          type="checkbox"
          checked={
            selectedIVR.length === IvrList.length && IvrList.length > 0
          }
          onChange={handleSelectAll}
        />
      ),
      enableSorting: false,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={selectedIVR.includes(row.original.flow_id)}
          onChange={() => handleCheckboxToggle(row.original.flow_id)}
        />
      ),
    },
    {
      accessorKey: 'flow_name',
      header: 'Flow Name',
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }: any) =>
        new Date(row.original.created_at).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
    },
    {
      accessorKey: 'updated_at',
      header: 'Last Updated At',
      cell: ({ row }: any) =>
        new Date(row.original.updated_at).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: any) => (
        <span>{getIvrStatusLabel(row.original.status)}</span>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      enableSorting: false,
      cell: ({ row }: any) => (
        <RenderActionComponent
          id={row.original.flow_id}
          category={row.original.ivr_category}
        />
      ),
    },
  ];

  return (
    <FillPage>
      {alertMessage && (
        <div
          className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-700 dark:text-emerald-400"
          role="status"
        >
          <span className="ivr-alert-marquee">
            <span
              className="ivr-alert-marquee-text"
              style={{ '--scroll-dur': `${Math.max(18, String(alertMessage).length * 0.16)}s` } as any}
            >
              {alertMessage}
            </span>
          </span>
        </div>
      )}
      <PageHeader
        title="IVRs"
        description="Interactive voice-response flows attached to inbound numbers."
        actions={
          canAdd && (
            <Button onClick={handleCreateButtonClick}>
              <Plus className="h-4 w-4" />
              Create IVR
            </Button>
          )
        }
      />

      <div>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Body className="px-0">
                <div className="scroll-container">
                  <div className="table-responsive border-bottom my-3">
                    <div className="mb-4 flex flex-wrap gap-2 px-3 pt-3">
                      {[
                        { key: 'normal', label: 'Normal' },
                        ...(canMissedIvr ? [{ key: 'missed', label: 'Missed' }] : []),
                      ].map((t) => {
                        const active = activeTab === t.key;
                        return (
                          <button
                            key={t.key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setActiveTab(t.key)}
                            className={
                              'flex h-9 items-center gap-2 rounded-lg border-2 px-4 text-sm font-bold transition ' +
                              (active
                                ? 'border-primary bg-primary text-primary-foreground shadow-md ring-2 ring-primary/25'
                                : 'border-primary/30 bg-primary/[0.06] text-primary hover:border-primary/50 hover:bg-primary/10')
                            }
                          >
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                    <DataTable
                      tableId="ivr.list"
                      columns={columns}
                      data={IvrList}
                      pageSize={10}
                      emptyTitle="No IVRs"
                      disableExport
                      leftContent={
                        <div className="flex flex-wrap items-center gap-2">
                          {canDelete && (
                            <Button
                              variant="danger"
                              style={{ height: '36px' }}
                              onClick={handleMultiDelete}
                              disabled={selectedIVR.length === 0}
                            >
                              Delete Selected
                            </Button>
                          )}
                          <input
                            className="form-control"
                            style={{
                              height: '36px',
                              width: '260px',
                              border: '1px solid #ced4da',
                              fontSize: '15px',
                            }}
                            placeholder="Search by Flow Name..."
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                          />
                          <Button
                            variant="primary"
                            style={{ height: '36px' }}
                            onClick={handleClear}
                          >
                            Refresh
                          </Button>
                        </div>
                      }
                      toolbar={
                        IvrList.length > 0 && (
                          <Button
                            variant="primary"
                            style={{ height: '36px' }}
                            onClick={handleExport}
                            disabled={buttonDisable}
                          >
                            Export Data &nbsp;
                            {buttonDisable && (
                              <span
                                className="spinner-border spinner-border-sm ml-2"
                                role="status"
                                aria-hidden="true"
                              />
                            )}
                          </Button>
                        )
                      }
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
        isMultiDelete={isMultiDelete}
        userLoadingDelete={userLoadingDelete}
        clearSelectedData={() => setSelectedIVR([])}
      />
    </FillPage>
  );
};

export default IvrList;
